import {
	type ChannelCatalogEntry,
	type ChannelPublishTag,
	CHANNEL_CATALOG_MAX_ENTRIES,
	buildChannelCatalogSummary,
	isLandscapeChannelMode,
	parseChannelPublishTag,
	parseStoryPublishTag,
	projectToChannelCatalogEntry,
	type VideoProject,
	VideoProjectSchema,
} from "@repo/types";
import { db } from "../client";
import { DBVideoProjectService } from "./dbVideoProject.service";

type CatalogVideoAspect = ChannelPublishTag["aspect"];
type CatalogVideoProjectFormat = ChannelPublishTag["format"];

function asRecord(value: unknown): Record<string, unknown> {
	return value && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {};
}

function resolveProjectAspect(
	document: Record<string, unknown>,
	inputBrief: Record<string, unknown>,
): CatalogVideoAspect | null {
	const fromDoc =
		typeof document.aspect === "string" ? document.aspect.trim() : "";
	if (
		fromDoc === "portrait" ||
		fromDoc === "landscape" ||
		fromDoc === "square"
	)
		return fromDoc;
	const fromBrief =
		typeof inputBrief.aspect === "string" ? inputBrief.aspect.trim() : "";
	if (
		fromBrief === "portrait" ||
		fromBrief === "landscape" ||
		fromBrief === "square"
	)
		return fromBrief;
	return null;
}

function resolveProjectFormat(
	document: Record<string, unknown>,
	inputBrief: Record<string, unknown>,
): CatalogVideoProjectFormat | null {
	const fromDoc =
		typeof document.format === "string" ? document.format.trim() : "";
	if (fromDoc === "story" || fromDoc === "documentary" || fromDoc === "news" || fromDoc === "advertisement")
		return fromDoc;
	const fromBrief =
		typeof inputBrief.format === "string" ? inputBrief.format.trim() : "";
	if (fromBrief === "story" || fromBrief === "documentary" || fromBrief === "news" || fromBrief === "advertisement")
		return fromBrief;
	return null;
}

function resolveProjectDurationMs(document: Record<string, unknown>): number {
	const raw = document.durationMs;
	if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0)
		return Math.round(raw);
	return 0;
}

function readSocialPostMetadata(metadata: unknown): {
	videoAppFileId?: string;
	videoProjectId?: string;
	jobId?: string;
} {
	const record = asRecord(metadata);
	const videoAppFileId =
		typeof record.videoAppFileId === "string"
			? record.videoAppFileId.trim()
			: undefined;
	const videoProjectId =
		typeof record.videoProjectId === "string"
			? record.videoProjectId.trim()
			: undefined;
	const jobId =
		typeof record.jobId === "string" ? record.jobId.trim() : undefined;
	return {
		...(videoAppFileId ? { videoAppFileId } : {}),
		...(videoProjectId ? { videoProjectId } : {}),
		...(jobId ? { jobId } : {}),
	};
}

export const DBChannelCatalogService = {
	async resolveVideoProjectFromAppFile(
		organizationId: string,
		appFileId: string,
	): Promise<{
		appFileId: string;
		jobId: string;
		project: VideoProject;
	} | null> {
		const appFile = await db.appFile.findFirst({
			where: { id: appFileId, organizationId },
			select: { id: true, jobId: true },
		});
		if (!appFile) return null;

		let jobId = appFile.jobId;
		if (!jobId) {
			const render = await db.renderHistory.findFirst({
				where: { appFileId, organizationId },
				orderBy: { createdAt: "desc" },
				select: { jobId: true },
			});
			jobId = render?.jobId ?? null;
		}
		if (!jobId) return null;

		const project = await DBVideoProjectService.getByJobId(jobId);
		if (!project || project.organizationId !== organizationId) return null;

		return { appFileId, jobId, project };
	},

	async listForOrganization(input: {
		organizationId: string;
		aspect?: CatalogVideoAspect | string;
		limit?: number;
	}): Promise<ChannelCatalogEntry[]> {
		const limit = Math.min(
			input.limit ?? CHANNEL_CATALOG_MAX_ENTRIES,
			CHANNEL_CATALOG_MAX_ENTRIES,
		);
		const entries: ChannelCatalogEntry[] = [];
		const batchSize = 50;
		let skip = 0;

		while (entries.length < limit) {
			const rows = await db.videoProject.findMany({
				where: {
					organizationId: input.organizationId,
					isDeleted: false,
					publishedAt: { not: null },
				},
				select: {
					id: true,
					title: true,
					document: true,
					inputBrief: true,
					episodeSummary: true,
					publishedAt: true,
					publishTag: true,
				},
				orderBy: { publishedAt: "desc" },
				skip,
				take: batchSize,
			});
			if (rows.length === 0) break;

			for (const row of rows) {
				const entry = projectToChannelCatalogEntry({
					id: row.id,
					title: row.title,
					document: row.document as Record<string, unknown> | null,
					inputBrief: row.inputBrief as Record<string, unknown> | null,
					episodeSummary: row.episodeSummary,
					publishedAt: row.publishedAt,
					publishTag: row.publishTag,
				});
				if (!entry) continue;
				if (input.aspect && entry.aspect !== input.aspect) continue;
				entries.push(entry);
				if (entries.length >= limit) break;
			}

			if (rows.length < batchSize) break;
			skip += batchSize;
		}

		return entries;
	},

	async publishChannelVideo(
		projectId: string,
		options: {
			publishedVia: ChannelPublishTag["publishedVia"];
			socialPostId?: string;
			publishedAt?: Date;
		},
	): Promise<{ project: VideoProject | null; reason?: string }> {
		const project = await DBVideoProjectService.getById(projectId);
		if (!project) return { project: null, reason: "project_not_found" };

		const document = asRecord(project.document);
		const inputBrief = asRecord(project.inputBrief);
		const aspect = resolveProjectAspect(document, inputBrief);
		const format = resolveProjectFormat(document, inputBrief);
		if (!aspect || !format)
			return { project: null, reason: "missing_aspect_or_format" };
		if (!isLandscapeChannelMode({ aspect, format }))
			return { project: null, reason: "not_landscape_story" };

		const legacyStoryTag = parseStoryPublishTag(project.publishTag);
		if (legacyStoryTag?.type === "story")
			return { project: null, reason: "legacy_story_episode" };

		const existingTag = parseChannelPublishTag(project.publishTag);
		if (existingTag?.type === "channel_video") {
			if (
				options.socialPostId &&
				existingTag.socialPostId === options.socialPostId
			)
				return { project };
			if (project.publishedAt)
				return { project, reason: "already_published" };
		}

		const title =
			(typeof document.title === "string" && document.title.trim()) ||
			project.title;
		const logline =
			typeof document.logline === "string" ? document.logline.trim() : "";
		const narrator = asRecord(inputBrief.narrator);
		const channelName =
			typeof narrator.channelName === "string"
				? narrator.channelName.trim()
				: undefined;
		const narratorProfileId =
			typeof inputBrief.narratorProfileId === "string"
				? inputBrief.narratorProfileId.trim()
				: undefined;

		const publishTag: ChannelPublishTag = {
			type: "channel_video",
			aspect,
			durationMs: resolveProjectDurationMs(document),
			format,
			publishedVia: options.publishedVia,
			...(options.socialPostId ? { socialPostId: options.socialPostId } : {}),
			...(channelName ? { channelName } : {}),
			...(narratorProfileId ? { narratorProfileId } : {}),
		};
		const episodeSummary = buildChannelCatalogSummary({ title, logline });
		const publishedAt = options.publishedAt ?? new Date();

		const result = await db.videoProject.update({
			where: { id: projectId },
			data: {
				publishedAt,
				publishTag,
				episodeSummary,
			},
		});
		return { project: VideoProjectSchema.parse(result) };
	},

	async indexFromSocialPublish(input: {
		socialPostId: string;
		publishedAt?: Date;
	}): Promise<{
		indexed: boolean;
		videoProjectId?: string;
		reason?: string;
	}> {
		const socialPost = await db.organizationSocialPost.findUnique({
			where: { id: input.socialPostId },
			select: {
				id: true,
				organizationId: true,
				metadata: true,
			},
		});
		if (!socialPost)
			return { indexed: false, reason: "social_post_not_found" };

		const link = readSocialPostMetadata(socialPost.metadata);
		let projectId = link.videoProjectId;

		if (!projectId && link.videoAppFileId) {
			const resolved = await this.resolveVideoProjectFromAppFile(
				socialPost.organizationId,
				link.videoAppFileId,
			);
			projectId = resolved?.project.id;
		}

		if (!projectId && link.jobId) {
			const project = await DBVideoProjectService.getByJobId(link.jobId);
			if (project?.organizationId === socialPost.organizationId)
				projectId = project.id;
		}

		if (!projectId)
			return { indexed: false, reason: "video_project_not_linked" };

		const { project, reason } = await this.publishChannelVideo(projectId, {
			publishedVia: "social",
			socialPostId: socialPost.id,
			publishedAt: input.publishedAt,
		});

		if (!project)
			return { indexed: false, videoProjectId: projectId, reason };

		return {
			indexed: true,
			videoProjectId: project.id,
			...(reason ? { reason } : {}),
		};
	},
};
